import React from 'react';
import { cloneDeep, isEmpty } from 'lodash';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useComponent } from '../../UserSetting/UsersLibrary/UserRights';
import _CommonMultiSelect from '../../../Components/Utility/FilterAction/CommonMultiSelect';
import _CustomTooltip from '../../../Components/Tooltip/CustomTooltip';
import '../SystemMasters.css';
import { ICustomAttributeMaster, ICustomAttributeOption } from './customAttribute.types';

interface ICommonMultiSelectProps {
  label: React.ReactNode;
  required?: boolean;
  onChange: (selected: unknown, index?: number, label?: string) => void;
  selectedValue: unknown;
  optionList: unknown;
  selectLabel: string;
  selectValue: string;
  className?: string;
  disable?: boolean;
  index?: number;
  dataPlaceholder?: string;
  fromUser?: boolean;
  optionListData?: unknown;
}
const CommonMultiSelect = _CommonMultiSelect as unknown as React.FC<ICommonMultiSelectProps>;
const CustomTooltip = _CustomTooltip as unknown as React.FC<{ tooltipText: React.ReactNode; placement?: string; children?: React.ReactNode }>;

interface IPcType {
  _id: string;
  name: string;
  isActive?: boolean;
}
interface IBrand {
  _id: string;
  name: string;
  isActive?: boolean;
}
interface IRootState {
  Users: { logInRights: unknown };
  SystemMastersDetails: { pcTypeList: IPcType[]; brandList: IBrand[] };
}

interface IRadioButtonCardProps {
  item: ICustomAttributeMaster;
  index: number;
  handleChanged: (e: React.ChangeEvent<HTMLInputElement>, index: number) => void;
  addValueForLabel: (id: string, index: number) => void;
  activeInactiveClicked: (id: string) => void;
  handleDelete: (idOrIndex: string | number, parentIndex?: number) => void;
  handleValueChanged: (
    e: React.ChangeEvent<HTMLInputElement>,
    optionIndex: number,
    parentIndex: number,
  ) => void;
  openList: number[];
  setOpenList: (next: number[]) => void;
  handleCheckboxCA: (e: React.ChangeEvent<HTMLInputElement>) => void;
  masterAddEditAccess: boolean;
  masterDeleteAccess: boolean;
  onDropSelectionChange: (e: unknown, index: number) => void;
  onDropBrandChange: (e: unknown, index: number) => void;
  isUniqueId?: number[] | undefined;
}

const RadioButtonCard: React.FC<IRadioButtonCardProps> = ({
  item,
  index,
  handleChanged,
  addValueForLabel,
  activeInactiveClicked,
  handleDelete,
  handleValueChanged,
  openList,
  setOpenList,
  handleCheckboxCA,
  masterAddEditAccess,
  masterDeleteAccess,
  onDropSelectionChange,
  onDropBrandChange,
  isUniqueId,
}) => {
  const { t: _t } = useTranslation('common');
  const t = _t as unknown as TranslateFn; // TranslateFn declared globally in src/global.d.ts
  const { CheckActionPermission } = useComponent();
  const reducer = useSelector((s) => s as unknown as IRootState);
  const {logInRights} = reducer.Users;
  const pcTypeList = reducer.SystemMastersDetails.pcTypeList ?? [];
  const brandList = reducer.SystemMastersDetails.brandList ?? [];

  type CheckActionPermissionFn = (rights: unknown, section: string, module?: string, action?: string) => boolean;
  const checkPermission = CheckActionPermission as unknown as CheckActionPermissionFn;
  const MasterAddEditAccess: boolean = checkPermission(
    logInRights,
    'masters',
    'customAttribute',
    'addEdit',
  );

  const productType: string[] = item.productType ?? [];
  const brand: string[] = item.brand ?? [];
  const values: ICustomAttributeOption[] = item.values ?? [];

  const handleOpenLabelOption = (optionIndex: number): void => {
    let data = cloneDeep(openList);
    if (data.includes(optionIndex)) {
      const indexOf = data.indexOf(optionIndex);
      if (indexOf > -1) data.splice(indexOf, 1);
    } else {
      data = [index, ...data];
    }
    setOpenList(data);
  };

  return (
    <div className="d-flex align-items-center justify-content-center">
      {item._id !== '' && (
        <div className="common-checkbox ml-2">
          <input
            type="checkbox"
            name={`checkbox_RB${index}`}
            id={`customAttribute_RB_${item._id}`}
            onChange={handleCheckboxCA}
            checked={item.isChecked === true}
          />
          <label htmlFor={`customAttribute_RB_${item._id}`} className="chklabel">
            <span />
          </label>
        </div>
      )}
      <div className="card listing-card mx-12 p-2 mb-2 flex-fill" key={index}>
        <div className="dropdown-container">
          <div className="input-container">
            <div className="input-group form-floating-label fixed-label">
              <input
                key={index}
                name="freeLabel"
                autoFocus={!item._id}
                value={item.keyName ?? ''}
                placeholder={t('RADIO_BUTTON_CARD.ENTER_LABEL')}
                type="text"
                onChange={(e) => handleChanged(e, index)}
                className="form-control"
                disabled={!MasterAddEditAccess}
              />
              <label htmlFor="text" className="text-bold f-16 asterisk-req">
                {t('RADIO_BUTTON_CARD.LABEL')}
                <span>*</span>
              </label>
            </div>
            {item.isEmpty && (item.keyName === null || item.keyName === '') && (
              <label className="text-danger m-0 p-1">{t('RADIO_BUTTON_CARD.LABEL_REQUIRED')}</label>
            )}
          </div>
          <div className="input-action d-flex">
            {item._id !== '' && masterAddEditAccess && (
              <>
                <CustomTooltip tooltipText={item.isActive ? 'Active' : 'Inactive'}>
                  <button
                    type="button"
                    className=" mr-0"
                    onClick={() => activeInactiveClicked(item._id)}
                    id={item._id}
                    aria-label={item.isActive ? 'Active' : 'Inactive'}
                  >
                    <span className={item.isActive ? 'icon-active f-24' : 'icon-inactive f-24'} />
                  </button>
                </CustomTooltip>

                <CustomTooltip tooltipText={values.length >= 2 ? t('RADIO_BUTTON_CARD.MAX_TWO_OPTIONS') : 'Add'}>
                  <button
                    type="button"
                    className=" mr-0"
                    onClick={() => addValueForLabel(item._id, index)}
                    id={item._id}
                    aria-label="Add"
                    disabled={values.length >= 2}
                  >
                    <span className="icon-Add f-24" />
                  </button>
                </CustomTooltip>
              </>
            )}

            <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ADD_OPTION')}>
              <button
                type="button"
                className="position-relative mr-0"
                onClick={() => handleOpenLabelOption(index)}
                aria-label="toggle options"
              >
                {openList.includes(index) ? (
                  <span className="icon-new-down-arrow-icon f-24 icon-dropdown icon-inverse" />
                ) : (
                  <span className="icon-new-down-arrow-icon f-24 icon-dropdown" />
                )}
              </button>
            </CustomTooltip>
            {masterDeleteAccess && (
              <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.DELETE')}>
                <button
                  type="button"
                  className=" mr-0"
                  onClick={() => handleDelete(item._id !== '' ? item._id : index)}
                  aria-label="delete"
                >
                  <span className="icon-Delete f-24" />
                </button>
              </CustomTooltip>
            )}
          </div>
        </div>

        {!isEmpty(isUniqueId) && isUniqueId?.includes(index) === true && (
          <label className="text-danger m-0 p-1">{t('RADIO_BUTTON_CARD.ENTRY_EXIST')}</label>
        )}

        <div className="common-react-select mb-1">
          <CommonMultiSelect
            label={t('RADIO_BUTTON_CARD.PRODUCT_TYPE')}
            required
            onChange={(e: unknown) => onDropSelectionChange(e, index)}
            selectedValue={productType}
            optionList={pcTypeList.map((e) => ({
              ...e,
              disabled:
                item._id !== '' &&
                item.isUsedProductType?.includes(e._id?.toString()) === true &&
                productType.includes(e._id?.toString()),
            }))}
            selectLabel="name"
            selectValue="_id"
            className="product-custom-multiselect common-new-multiselect w-100 text-bold mt-2"
            disable={!MasterAddEditAccess}
          />
          {item.isEmpty && productType.length === 0 && (
            <label className="text-danger m-0 p-1">{t('RADIO_BUTTON_CARD.PRODUCT_TYPE_REQUIRED')}</label>
          )}
        </div>

        <div className="common-react-select mb-1">
          <CommonMultiSelect
            label="Brand"
            required
            onChange={(e: unknown) => onDropBrandChange(e, index)}
            selectedValue={brand}
            optionList={brandList
              .filter((e) => e.isActive === true)
              .map((e) => ({
                ...e,
                disabled: item._id !== '' && item.isUsedBrand?.includes(e._id?.toString()) === true,
              }))}
            selectLabel="name"
            selectValue="_id"
            className="product-custom-multiselect common-new-multiselect w-100 text-bold mt-2"
            disable={!MasterAddEditAccess}
          />
          {item.isEmpty && brand.length === 0 && (
            <label className="text-danger m-0 p-1">{t('RADIO_BUTTON_CARD.BRAND_REQUIRED')}</label>
          )}
        </div>

        {values.length > 0 ? (
          values.map((eleValue, indexValue) => (
            <div
              key={eleValue._id !== '' ? eleValue._id : `opt-${indexValue}`}
              className={openList.includes(index) ? 'dropdown-items show' : 'dropdown-items '}
            >
              <div className="freetext-container mt-2 mx-0">
                <div className="input-container">
                  <div className="input-group form-floating-label fixed-label">
                    <input
                      key={eleValue._id !== '' ? eleValue._id : `opt-input-${indexValue}`}
                      name="keyName"
                      autoFocus={!eleValue._id}
                      disabled={!masterAddEditAccess}
                      value={eleValue.keyName ?? ''}
                      placeholder={t('RADIO_BUTTON_CARD.ENTER_VALUE')}
                      type="text"
                      onChange={(e) => handleValueChanged(e, indexValue, index)}
                      className="form-control"
                    />
                    <label htmlFor="text" className="asterisk-req">
                      {t('RADIO_BUTTON_CARD.VALUE')} {indexValue + 1}
                      <span>*</span>
                    </label>
                  </div>
                </div>
                <div className="input-action d-flex">
                  {eleValue._id !== '' && masterAddEditAccess && (
                    <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.ACTIVE_INACTIVE')}>
                      <button
                        type="button"
                        aria-label="Active"
                        className="btn p-0 custom-action mr-0"
                        onClick={() => activeInactiveClicked(eleValue._id)}
                        id={eleValue._id}
                      >
                        <span
                          className={eleValue.isActive ? 'icon-active f-24' : 'icon-inactive f-24'}
                        />
                      </button>
                    </CustomTooltip>
                  )}
                  {masterDeleteAccess && (
                    <CustomTooltip tooltipText={t('CUSTOM_ATTRIBUTES.DELETE')}>
                      <button
                        className=" mr-0"
                        type="button"
                        aria-label="Delete"
                        onClick={() =>
                          handleDelete(eleValue._id !== '' ? eleValue._id : indexValue, index)
                        }
                      >
                        <span className="icon-Delete f-24" />
                      </button>
                    </CustomTooltip>
                  )}
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className={openList.includes(index) ? 'dropdown-items show' : 'dropdown-items '}>
            <p className="mt-3 mb-0 text-center">{t('RADIO_BUTTON_CARD.NO_OPTIONS_FOUND')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default RadioButtonCard;
